import React, { useEffect } from 'react';
import { injectPulseAnimations, BADGE_COLORS } from '../utils/pulseAnimations';
import { NEUTRAL_PALETTE } from '../utils/neutralPalette';

interface TalkingAvatarProps {
  isRecording?: boolean;
  isSpeaking?: boolean;
  size?: number;
  avatarUrl?: string;
  tone?: 'default' | 'neutral';
}

const TalkingAvatar: React.FC<TalkingAvatarProps> = ({ 
  isRecording = false, 
  isSpeaking = false, 
  size = 48,
  avatarUrl,
  tone = 'default'
}) => {
  const getAvatarState = () => {
    if (isRecording) return 'recording';
    if (isSpeaking) return 'speaking';
    return 'idle';
  };

  const state = getAvatarState();
  const useNeutralTone = tone === 'neutral';
  const background = avatarUrl ? 'transparent' :
    useNeutralTone
      ? NEUTRAL_PALETTE.pearl
      : state === 'recording'
        ? `linear-gradient(45deg, ${BADGE_COLORS.recording}, #dc2626)`
        : state === 'speaking'
        ? `linear-gradient(45deg, ${BADGE_COLORS.active}, #059669)`
        : 'linear-gradient(45deg, #6366f1, #4f46e5)';
  const ringColor = useNeutralTone
    ? state === 'idle'
      ? NEUTRAL_PALETTE.mist
      : state === 'recording'
        ? NEUTRAL_PALETTE.anthracite
        : NEUTRAL_PALETTE.slate
    : state === 'recording'
      ? BADGE_COLORS.recording
      : BADGE_COLORS.active;
  const neutralShadow = state === 'idle'
    ? `0 0 0 3px ${NEUTRAL_PALETTE.mist}`
    : `0 0 0 3px ${ringColor}, 0 0 18px rgba(94, 93, 89, 0.18)`;

  // Inject pulse animation styles and custom styles
  useEffect(() => {
    injectPulseAnimations('talking-avatar-pulse-animations');

    const styleId = 'talking-avatar-custom-styles';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes talking-avatar-speaking {
          0% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1.15);
          }
        }

        .talking-avatar--speaking {
          animation: talking-avatar-speaking 0.5s infinite alternate;
        }

        .talking-avatar-pulse-ring {
          animation: avatar-button-pulse-blue 2s infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  return (
    <div 
      className={`talking-avatar talking-avatar--${state}`}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: useNeutralTone ? NEUTRAL_PALETTE.anthracite : 'white',
        fontSize: size * 0.4,
        fontWeight: 'bold',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        boxShadow: useNeutralTone
          ? neutralShadow
          : state === 'recording'
          ? `0 0 20px ${BADGE_COLORS.recording}80`
          : state === 'speaking'
          ? `0 0 20px ${BADGE_COLORS.active}80`
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
        transform: state === 'recording' || state === 'speaking' ? 'scale(1.1)' : 'scale(1)',
        overflow: 'hidden',
      }}
    >
      {avatarUrl ? (
        <img 
          src={avatarUrl} 
          alt="Avatar"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: '50%',
          }}
        />
      ) : (
        useNeutralTone ? 'AI' : state === 'recording' ? '🎤' : state === 'speaking' ? '🗣️' : '🤖'
      )}
      
      {(state === 'recording' || state === 'speaking') && (
        <div
          className="talking-avatar-pulse-ring"
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            border: `2px solid ${ringColor}`,
          }}
        />
      )}
    </div>
  );
};

export default TalkingAvatar;
